import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const AUTO_DISMISS_MS = 2500;

interface Toast {
  /**
   * 用于在连续显示相同内容时更换节点的标识符。
   *
   * 如果仅将文本保留在状态中，React 将跳过相同值的重新渲染，导致 DOM 保持不变。
   * 由于屏幕阅读器通过识别 live region 的变化来播报，如果没有变化，它就什么都不读。
   * 在本界面中，连续复制账户号码是正常流程，且成功提示消息每次都相同。
   */
  readonly id: number;
  readonly message: string;
}

interface ToastContextValue {
  showToast: (message: string) => void;
  /**
   * 指定渲染通知的位置。
   *
   * 带有 aria-modal="true" 的对话框会将自己外部的所有内容从辅助技术中排除。
   * 如果通知区域仍留在应用树中，模态框内发生的复制结果将无法传达给屏幕阅读器，
   * 因此在模态框打开期间，将其移动到对话框内部进行渲染。
   */
  setLiveRegionHost: (host: HTMLElement | null) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * 替代 antd 的 message 通知。
 *
 * 之前的实现只是在屏幕上短暂出现的方框，屏幕阅读器用户完全无法得知“账户号码已复制”的事实。
 * 点击复制按钮没有任何反馈，因此无法判断操作是否成功。
 *
 * 我们始终渲染带有 aria-live="polite" 的通知区域，仅更新其中的内容。
 * 这是因为如果延迟插入区域本身，部分屏幕阅读器可能会错过变化。
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextIdRef = useRef(0);

  const showToast = useCallback((message: string) => {
    nextIdRef.current += 1;
    setToast({ id: nextIdRef.current, message });

    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setToast(null), AUTO_DISMISS_MS);
  }, []);

  const setLiveRegionHost = useCallback((next: HTMLElement | null) => {
    setHost(next);
  }, []);

  /* 卸载时清理定时器，避免更新已消失组件的状态。 */
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  const value = useMemo(
    () => ({ showToast, setLiveRegionHost }),
    [showToast, setLiveRegionHost],
  );

  const liveRegion = (
    <LiveRegion role="status" aria-live="polite" aria-atomic="true">
      {toast !== null && <Bubble key={toast.id}>{toast.message}</Bubble>}
    </LiveRegion>
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {host === null ? liveRegion : createPortal(liveRegion, host)}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);

  if (context === null) {
    throw new Error("useToast 只能在 ToastProvider 内使用。");
  }

  return context;
}

const LiveRegion = styled.div`
  position: fixed;
  top: ${({ theme }) => theme.space[6]};
  left: 50%;
  transform: translateX(-50%);
  z-index: ${({ theme }) => theme.zIndex.toast};
  /* 当没有通知时，确保透明方框不会拦截点击事件。 */
  pointer-events: none;
`;

const Bubble = styled.div`
  /*
   * 失败提示文字长达 30 个字符，如果固定为单行，在窄屏幕上会被截断。
   * 而恰好被截断的部分往往是告知“可以采取什么替代操作”的句子。
   */
  max-width: calc(100vw - ${({ theme }) => theme.space[8]});
  padding: ${({ theme }) => theme.space[3]} ${({ theme }) => theme.space[6]};
  border-radius: 16px;
  background-color: rgba(0, 0, 0, 0.82);
  color: #ffffff;
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: center;
  word-break: keep-all;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
`;
